import { useState, useCallback, useMemo } from 'react';

const DEFAULT_TITLE = 'Untitled Document';

function generateId() {
  return `doc_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

function countWords(text) {
  if (!text || typeof text !== 'string') return 0;
  const cleaned = text.replace(/[#*`_\[\]()]/g, '').trim();
  if (!cleaned) return 0;
  return cleaned.split(/\s+/).filter(Boolean).length;
}

function generatePreview(content) {
  if (!content || typeof content !== 'string') return '';
  const plainText = content
    .replace(/[#*`_]/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
  const maxLength = 200;
  return plainText.length > maxLength
    ? plainText.substring(0, maxLength) + '...'
    : plainText;
}

/**
 * Hook for in-memory chat document management (no API calls).
 * Replaces chatDocumentsStore for HomePageV2; state is lost on reload.
 */
export function useChatDocuments() {
  const [documents, setDocuments] = useState([]);
  const [currentDocumentId, setCurrentDocumentIdState] = useState(null);

  const addDocument = useCallback((docData) => {
    const id = generateId();
    const now = new Date();
    const contentString =
      typeof docData.content === 'string'
        ? docData.content
        : String(docData.content ?? '');
    const newDocument = {
      id,
      title: docData.title || DEFAULT_TITLE,
      content: contentString,
      createdAt: now,
      lastEditedAt: now,
      wordCount: countWords(contentString),
      preview: generatePreview(contentString),
      tags: docData.tags || [],
      agentUuid: docData.agentUuid ?? null,
      conversationId: docData.conversationId ?? null,
      messageId: docData.messageId ?? null,
      module: docData.module || 'HomePageV2',
    };
    setDocuments((prev) => [...prev, newDocument]);
    return id;
  }, []);

  const updateDocument = useCallback((id, updates) => {
    setDocuments((prev) => {
      const index = prev.findIndex((doc) => doc.id === id);
      if (index === -1) return prev;
      const doc = prev[index];
      const nextUpdates = { ...updates };
      if (updates.content !== undefined && updates.content !== null) {
        const contentString =
          typeof updates.content === 'string'
            ? updates.content
            : String(updates.content);
        nextUpdates.lastEditedAt = new Date();
        nextUpdates.wordCount = countWords(contentString);
        nextUpdates.preview = generatePreview(contentString);
        nextUpdates.content = contentString;
      }
      const next = [...prev];
      next[index] = { ...doc, ...nextUpdates };
      return next;
    });
  }, []);

  const deleteDocument = useCallback((id) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    setCurrentDocumentIdState((prev) => (prev === id ? null : prev));
  }, []);

  const getDocument = useCallback(
    (id) => documents.find((doc) => doc.id === id) || null,
    [documents]
  );

  const getDocumentByMessageId = useCallback(
    (messageId, conversationId) =>
      documents.find(
        (doc) =>
          doc.messageId === messageId &&
          (conversationId == null || doc.conversationId === conversationId)
      ) || null,
    [documents]
  );

  const setCurrentDocument = useCallback((id) => {
    setCurrentDocumentIdState(id);
  }, []);

  const clearCurrentDocument = useCallback(() => {
    setCurrentDocumentIdState(null);
  }, []);

  const sortedDocuments = useMemo(
    () =>
      [...documents].sort(
        (a, b) => new Date(b.lastEditedAt) - new Date(a.lastEditedAt)
      ),
    [documents]
  );

  const documentCount = documents.length;

  const currentDocument = useMemo(
    () => (currentDocumentId ? getDocument(currentDocumentId) : null),
    [currentDocumentId, getDocument]
  );

  const searchDocuments = useCallback(
    (query) => {
      if (!query || !query.trim()) return sortedDocuments;
      const lower = query.toLowerCase();
      return documents
        .filter(
          (doc) =>
            doc.title.toLowerCase().includes(lower) ||
            (doc.content && doc.content.toLowerCase().includes(lower)) ||
            (doc.tags && doc.tags.some((t) => t.toLowerCase().includes(lower))) ||
            (doc.preview && doc.preview.toLowerCase().includes(lower))
        )
        .sort((a, b) => new Date(b.lastEditedAt) - new Date(a.lastEditedAt));
    },
    [documents, sortedDocuments]
  );

  return {
    documents,
    currentDocumentId,
    currentDocument,
    sortedDocuments,
    documentCount,
    addDocument,
    updateDocument,
    deleteDocument,
    getDocument,
    getDocumentByMessageId,
    setCurrentDocument,
    clearCurrentDocument,
    searchDocuments,
  };
}
